"use client";

import { Check, XIcon } from "lucide-react";
import { useMemo, useRef, useState, type ReactNode } from "react";

import { cn } from "@/lib/utils";
import type { Feature, FeatureGroup, Plan } from "@/types";
import type { SubscriptionFrequency } from "@/app/subscription/[frequency]/types";
import { Currency, formatCurrency } from "@/lib/utils/currency";
import { useCurrency } from "@/lib/contexts/CurrencyContext";

import { PricingSummaryInfoDialog } from "@/components/pricing/PricingSummaryInfoDialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import PlanButtonHeader from "./PlanButtonHeader";
import { PlanButtonShell } from "./PlanButton";

type Props = {
	plan: Plan;
	frequency: SubscriptionFrequency;
	renderPlanButton?: ReactNode;
	renderPost?: ReactNode;
	/**
	 * Admin list: toolbar + non-checkout "Select" shell are rendered inside this client component
	 * (avoids passing RSC-built nodes into `CardHeader` and triggering key warnings).
	 */
	adminToolbar?: boolean;
	/** Show every feature row (no "See More"). Defaults true when `plan.planType === "hosting"`. */
	expandFeatureList?: boolean;
	className?: string;
};

export default function PlanCard({
	plan,
	frequency,
	renderPlanButton,
	renderPost,
	adminToolbar,
	expandFeatureList,
	className,
}: Props) {
	const price = plan.prices.find((price) => price.duration.toLowerCase() === frequency)!;
	const currency = useCurrency();
	
	// Get price amount for user's currency (prefer new multi-currency structure)
	const priceAmount = price.amounts?.[currency] ?? price.amount ?? 0;

	const showAllFeatures =
		expandFeatureList === true || plan.planType === "hosting";

	return (
		<Card
			className={cn(
				"flex w-72 flex-col h-fit rounded-[20px] border-none !shadow-custom snap-start",
				className,
			)}
			style={{
				backgroundColor: plan.colors.bgColor,
				color: plan.colors.fontColor,
			}}
		>
			<div className="flex min-h-0 flex-col">
				{adminToolbar ? (
					<PlanButtonHeader key={`plan-admin-header-${plan._id}`} plan={plan} />
				) : (
					renderPost
				)}
				<CardHeader className="flex w-full flex-col items-center gap-y-4 bg-transparent pb-2 pt-8 text-center">
					<div className="min-h-24 space-y-6">
						<CardTitle className="text-2xl font-semibold">{plan.name}</CardTitle>
						<Price
							amount={priceAmount}
							currency={currency}
							frequency={frequency}
							priceColor={plan.colors.priceColor}
						/>
					</div>

					{adminToolbar ? (
						<PlanButtonShell
							key={`plan-admin-select-${plan._id}`}
							buttonColor={plan.colors.buttonColor}
							disabled={false}
						>
							Select
						</PlanButtonShell>
					) : (
						renderPlanButton
					)}
				</CardHeader>
				<span className="my-4 self-start px-6 text-sm">This includes:</span>
				<FeatureGroups
					featureGroups={plan.featureGroups ?? []}
					textColor={plan.colors.fontColor}
					priceColor={plan.colors.priceColor}
					expandAll={showAllFeatures}
				/>
			</div>
		</Card>
	);
}

type PriceProps = {
	amount: number;
	currency: Currency;
	frequency: SubscriptionFrequency;
	priceColor: string;
};

export function Price({ amount, currency, frequency, priceColor }: PriceProps) {
	if (amount === 0) {
		return (
			<CardTitle className="text-lg" style={{ color: priceColor }}>
				Free
			</CardTitle>
		);
	}

	const frequencySyntax = frequency === "monthly" ? "month" : "year";
	const formattedPrice = formatCurrency(amount, currency);

	return (
		<CardTitle aria-label={`${amount} per ${frequency}`} className="ml-2 text-base" suppressHydrationWarning>
			<span className="text-3xl" style={{ color: priceColor }} suppressHydrationWarning>
				{formattedPrice}
			</span>
			<span className="ml-1 mr-0.5">/</span>
			<span className="text-sm3">{frequencySyntax}</span>
		</CardTitle>
	);
}

type FeatureGroupsProps = {
	featureGroups: FeatureGroup[];
	textColor: string;
	priceColor: string;
	expandAll?: boolean;
};
export function FeatureGroups({
	featureGroups: groups,
	textColor,
	priceColor,
	expandAll = false,
}: FeatureGroupsProps) {
	return (
		<CardContent className="space-y-4 pb-8">
			{groups.map((group, groupIndex) => (
				<FeatureGroupList
					key={`${groupIndex}-${group.title}`}
					group={group}
					textColor={textColor}
					priceColor={priceColor}
					expandAll={expandAll}
				/>
			))}
		</CardContent>
	);
}

function calculateEndingAddress(features: Feature[], limit: number) {
	let count = 0;
	let address: [number, number] = [-1, -1];

	for (let a = 0; a < features.length; a++) {
		const { attributes } = features[a];

		count++;
		if (count === limit) {
			address = [a, -1];
			break;
		}

		for (let b = 0; b < attributes.length; b++) {
			count++;
			if (count === limit) {
				address = [a, b];
				break;
			}
		}
	}

	return address;
}

type FeatureGroupListProps = {
	group: FeatureGroup;
	textColor: string;
	priceColor: string;
	expandAll?: boolean;
};

export function FeatureGroupList({
	group,
	textColor,
	priceColor,
	expandAll = false,
}: FeatureGroupListProps) {
	const [expanded, setExpansion] = useState(false);

	const limit = 4;
	const cached = useRef<Feature[] | null>(null);

	useMemo(() => {
		if (expandAll) {
			cached.current = null;
			return;
		}
		if (group.title === "General Benefits") return;

		const [featureIndex, attributeIndex] = calculateEndingAddress(group.features, limit);
		if (featureIndex === -1 || (featureIndex === -1 && attributeIndex === -1)) return;

		const features = structuredClone(group.features.slice(0, featureIndex + 1));
		const feature = features[featureIndex];
		feature.attributes = feature.attributes.slice(0, attributeIndex + 1);

		cached.current = features;
		return () => {
			cached.current = null;
		};
	}, [expandAll, group.title, group.features]);

	const items =
		expandAll || cached.current === null || expanded ? group.features : cached.current;

	return (
		<div className="flex flex-col gap-2 text-sm">
			<h2 className="font-bold">{group.title}</h2>
			{items?.map((x, featureIndex) => (
				<FeatureItem
					key={x._id ?? `${group.title}-${featureIndex}-${x.name}`}
					textColor={textColor}
					priceColor={priceColor}
					feature={x}
				/>
			))}

			{!expandAll && cached.current !== null && (
				<span
					role="button"
					className="flex h-5 justify-end text-xs"
					onClick={() => setExpansion(!expanded)}
				>
					{expanded ? "See less" : "See More"}
				</span>
			)}
		</div>
	);
}

type FeatureItemProps = {
	feature: Feature;
	priceColor: string;
	textColor: string;
};

export function FeatureItem({
	feature: { name, status, attributes, description },
	textColor,
	priceColor,
}: FeatureItemProps) {
	return (
		<div className="grid grid-cols-[1.25rem_1fr_1.25rem] gap-x-2">
			<div className="inline-flex size-5 items-center justify-center">
				{status === "RIGHT" && <Check size="1rem" stroke={priceColor} strokeWidth="4px" />}
				{status === "WRONG" && <XIcon size="1rem" stroke={textColor} strokeWidth="4px" />}
				{status === "NONE" && (
					<span color={textColor} className="text-xs font-semibold">
						{"--"}
					</span>
				)}
			</div>
			<div className="flex min-h-5 flex-col text-xsm2">
				<h3>{name}</h3>
			</div>
			{description.length > 0 && (
				<PricingSummaryInfoDialog
					ariaLabel={`More about ${name}`}
					title={name}
					iconSize="sm"
					className="size-5"
					iconStyle={{ color: textColor }}
				>
					{description}
				</PricingSummaryInfoDialog>
			)}
			<ul className="col-span-full ml-12 list-disc space-y-2 pr-4 pt-2 text-xs">
				{attributes.map((line, lineIndex) => (
					<li key={`${name}-${lineIndex}-${line}`}>{line}</li>
				))}
			</ul>
		</div>
	);
}
